import WeaponSword from '../../assets/WeaponSword';

// Per-environment drop-shadow + hue-rotate for the tier boss attack weapon
const ENV_BOSS_ATTACK_FILTER = {
  forest:    'drop-shadow(0 0 10px rgba(0,220,80,0.95))   hue-rotate(95deg)',
  desert:    'drop-shadow(0 0 10px rgba(255,160,20,0.95)) hue-rotate(30deg)',
  snow:      'drop-shadow(0 0 10px rgba(80,180,255,0.95)) hue-rotate(195deg)',
  castle:    'drop-shadow(0 0 10px rgba(160,80,255,0.95)) hue-rotate(250deg)',
  water:     'drop-shadow(0 0 10px rgba(0,140,255,0.95))  hue-rotate(200deg)',
  graveyard: 'drop-shadow(0 0 10px rgba(140,255,100,0.95)) hue-rotate(80deg)',
  inferno:   'drop-shadow(0 0 10px rgba(255,60,0,0.95))   hue-rotate(0deg)',
};

/**
 * Renders all active spell visual effects (explosions, lightning, heals, etc.).
 * Props: spellEffects (array), now (performance.now() snapshot), env (environment name)
 */
export function SpellEffectsLayer({ spellEffects, now, env }) {
  if (!spellEffects?.length) return null;

  return spellEffects.map(eff => {
    const progress = Math.min(1, (now - eff.startTime) / eff.duration);
    const opacity  = 1 - progress;

    if (eff.type === 'explosion') {
      const rad = (eff.radius ?? 150) * (0.25 + 0.75 * progress);
      return (
        <div key={eff.id} className="spell-fx spell-fx--explosion" style={{
          left: eff.x - rad, top: eff.y - rad,
          width: rad * 2, height: rad * 2,
          opacity,
        }} />
      );
    }

    if (eff.type === 'lightning') {
      return (
        <div key={eff.id} className="spell-fx spell-fx--lightning" style={{
          left: eff.x - 3, top: eff.y - 70,
          opacity,
          transform: `scaleY(${1 - progress * 0.4})`,
        }} />
      );
    }

    if (eff.type === 'heal') {
      const rad = 20 + 60 * progress;
      return (
        <div key={eff.id} className="spell-fx spell-fx--heal" style={{
          left: eff.x - rad, top: eff.y - rad,
          width: rad * 2, height: rad * 2, opacity,
        }} />
      );
    }

    if (eff.type === 'vortex') {
      const rad = 20 + 110 * progress;
      return (
        <div key={eff.id} className="spell-fx spell-fx--vortex" style={{
          left: eff.x - rad, top: eff.y - rad,
          width: rad * 2, height: rad * 2, opacity,
          transform: `rotate(${progress * 540}deg)`,
        }} />
      );
    }

    if (eff.type === 'blink') {
      return (
        <div key={eff.id} className="spell-fx spell-fx--blink" style={{
          left: eff.x - 30, top: eff.y - 30, opacity,
          transform: `scale(${1 + progress})`,
        }} />
      );
    }

    if (eff.type === 'cone') {
      const coneProgress = Math.min(1, (now - eff.startTime) / eff.duration);
      const coneOpacity  = 1 - coneProgress;
      const currentRange = eff.range * (0.25 + 0.75 * coneProgress);
      const ha           = eff.halfAngle ?? (Math.PI / 4);
      const ang          = eff.angle ?? 0;
      const lx = eff.x + Math.cos(ang - ha) * currentRange;
      const ly = eff.y + Math.sin(ang - ha) * currentRange;
      const rx = eff.x + Math.cos(ang + ha) * currentRange;
      const ry = eff.y + Math.sin(ang + ha) * currentRange;
      return (
        <svg key={eff.id} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
          <polygon
            points={`${eff.x},${eff.y} ${lx},${ly} ${rx},${ry}`}
            fill={eff.color ?? 'rgba(255,100,0,0.5)'}
            stroke={eff.strokeColor ?? 'rgba(255,200,60,0.85)'}
            strokeWidth="2"
            strokeLinejoin="round"
            opacity={coneOpacity}
          />
        </svg>
      );
    }

    if (eff.type === 'tier_boss_attack') {
      const r2      = eff.radius ?? 150;
      const vw      = r2 * 2;
      const vh      = r2 * 2;
      // Weapon base at boss centre, height = attack radius
      const weaponH = r2;
      const weaponW = Math.max(20, Math.round(r2 * 0.30));
      const wx      = vw / 2 - weaponW / 2;
      const wy      = vh / 2 - weaponH;
      const bossFilter = ENV_BOSS_ATTACK_FILTER[env] ?? ENV_BOSS_ATTACK_FILTER.inferno;
      return (
        <div
          key={eff.id}
          className="attack"
          style={{
            position: 'absolute',
            left: eff.x - vw / 2,
            top:  eff.y - vh / 2,
            width: vw, height: vh,
            opacity,
          }}
        >
          <svg
            width={weaponW} height={weaponH}
            viewBox="0 0 25 25" fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: 'absolute', left: wx, top: wy,
              overflow: 'visible',
              filter: bossFilter,
            }}
          >
            <WeaponSword />
          </svg>
        </div>
      );
    }

    if (eff.type === 'kill_explosion') {
      const maxRad = eff.isBoss ? 55 : 28;
      const rad    = maxRad * progress;
      const op     = 1 - progress;
      return (
        <div key={eff.id} style={{
          position:     'absolute',
          left:         eff.x - rad,
          top:          eff.y - rad,
          width:        rad * 3,
          height:       rad * 3,
          borderRadius: '50%',
          border:       `2px solid ${eff.color}`,
          background:   `radial-gradient(circle, ${eff.color}88 0%, transparent 65%)`,
          boxShadow:    `0 0 ${maxRad * 0.4}px ${eff.color}`,
          opacity:      op,
          pointerEvents: 'none',
        }} />
      );
    }

    return null;
  });
}
